import {
  HELP_QUESTION_MAX_LENGTH,
  type AskHelpRequest,
  type AskHelpResponse,
  type CreateHelpFeedbackResponse,
  type HelpOperationsContext,
} from '@tcvs/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useHelpApi } from '../api';
import { buildInfo } from '../build-info';
import {
  helpDiagnosticStatusLabel,
  helpFeedbackRequest,
  helpHistory,
  helpSourceHref,
  type HelpTranscriptEntry,
} from './help-assistant-ui';
import { subscribeHelpOpen, subscribeOperationsHelpContext } from './operations-help-context';

export function HelpAssistant(): JSX.Element {
  const api = useHelpApi();
  const location = useLocation();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [entries, setEntries] = useState<HelpTranscriptEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operationsContext, setOperationsContext] = useState<HelpOperationsContext>();
  const currentRoute = `${location.pathname}${location.search}`;

  useEffect(() => subscribeOperationsHelpContext(setOperationsContext), []);

  useEffect(
    () =>
      subscribeHelpOpen((initialQuestion) => {
        setOpen(true);
        setQuestion(initialQuestion);
      }),
    [],
  );

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight });
  }, [entries, submitting]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  async function ask(): Promise<void> {
    const trimmed = question.trim();
    if (trimmed === '' || submitting) return;
    const priorEntries = entries;
    const userEntry: HelpTranscriptEntry = { role: 'user', content: trimmed };
    setEntries([...priorEntries, userEntry]);
    setQuestion('');
    setError(null);
    setSubmitting(true);

    const request: AskHelpRequest = {
      question: trimmed,
      currentRoute,
      history: helpHistory(priorEntries),
      clientRevision: buildInfo.revision,
      ...(location.pathname === '/admin/operations' && operationsContext !== undefined
        ? { operationsContext }
        : {}),
    };
    try {
      const result = await api.request<AskHelpResponse>('/help/ask', {
        method: 'POST',
        body: request,
      });
      if (!result.ok) {
        setError('Help is temporarily unavailable. Please try again.');
        return;
      }
      setEntries((current) => [
        ...current,
        {
          role: 'assistant',
          content: result.data.answer,
          sources: result.data.sources,
          diagnostic: result.data.diagnostic,
          interactionId: result.data.interactionId,
          question: trimmed,
          currentRoute,
          generatedBy: result.data.generatedBy,
          contentVersion: result.data.contentVersion,
        },
      ]);
    } catch {
      setError('Help is temporarily unavailable. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function reportUnhelpful(index: number): Promise<void> {
    const entry = entries[index];
    if (
      entry?.role !== 'assistant' ||
      entry.interactionId === undefined ||
      entry.question === undefined ||
      entry.feedbackState === 'submitting' ||
      entry.feedbackState === 'submitted'
    ) {
      return;
    }
    setEntries((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, feedbackState: 'submitting' } : item,
      ),
    );
    const body = helpFeedbackRequest(entry);
    if (body === undefined) return;
    try {
      const result = await api.request<CreateHelpFeedbackResponse>('/help/feedback', {
        method: 'POST',
        body,
      });
      setEntries((current) =>
        current.map((item, itemIndex) =>
          itemIndex === index
            ? { ...item, feedbackState: result.ok ? 'submitted' : 'error' }
            : item,
        ),
      );
    } catch {
      setEntries((current) =>
        current.map((item, itemIndex) =>
          itemIndex === index ? { ...item, feedbackState: 'error' } : item,
        ),
      );
    }
  }

  return (
    <>
      <button
        type="button"
        className="tcvs-help-trigger"
        aria-label="Open help"
        title="Help"
        onClick={() => setOpen(true)}
      >
        ?
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="tcvs-help-backdrop"
            aria-label="Close help"
            onClick={() => setOpen(false)}
          />
          <aside
            className="tcvs-help-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="TCVS help"
          >
            <header className="tcvs-help-header">
              <div>
                <strong>TCVS Help</strong>
                <span>Documented guidance and live status</span>
              </div>
              <button
                type="button"
                aria-label="Close help"
                title="Close"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </header>

            <div className="tcvs-help-transcript" ref={transcriptRef} aria-live="polite">
              {entries.length === 0 ? (
                <div className="tcvs-help-welcome">
                  <strong>How can I help?</strong>
                  <p>Ask how to use the page you are on or where to find a TCVS workflow.</p>
                </div>
              ) : null}
              {entries.map((entry, index) => (
                <article
                  className={`tcvs-help-message is-${entry.role}`}
                  key={`${entry.role}-${index}`}
                >
                  <span>{entry.role === 'user' ? 'You' : 'TCVS Help'}</span>
                  <p>{entry.content}</p>
                  {entry.diagnostic === undefined ? null : (
                    <section
                      className="tcvs-help-diagnostic"
                      data-status={entry.diagnostic.status}
                      aria-label="Live diagnostic"
                    >
                      <header>
                        <strong>Live diagnostic</strong>
                        <span>{helpDiagnosticStatusLabel(entry.diagnostic.status)}</span>
                      </header>
                      <p>{entry.diagnostic.summary}</p>
                      {entry.diagnostic.blockers.length === 0 ? null : (
                        <ul>
                          {entry.diagnostic.blockers.map((blocker) => (
                            <li key={blocker}>{blocker}</li>
                          ))}
                        </ul>
                      )}
                      <dl>
                        {entry.diagnostic.evidence.map((evidence) => (
                          <div key={evidence.label}>
                            <dt>{evidence.label}</dt>
                            <dd>{evidence.value}</dd>
                          </div>
                        ))}
                      </dl>
                      <nav aria-label="Diagnostic links">
                        {entry.diagnostic.links.map((link) => (
                          <Link
                            key={`${link.title}-${link.route}`}
                            to={link.route}
                            onClick={() => setOpen(false)}
                          >
                            {link.title}
                          </Link>
                        ))}
                      </nav>
                      <small>
                        Checked {new Date(entry.diagnostic.observedAt).toLocaleString()}
                      </small>
                    </section>
                  )}
                  {entry.sources !== undefined && entry.sources.length > 0 ? (
                    <div className="tcvs-help-sources">
                      <span>Open in TCVS</span>
                      {entry.sources.map((source) => (
                        <Link
                          key={source.id}
                          to={helpSourceHref(source, currentRoute)}
                          onClick={() => setOpen(false)}
                        >
                          {source.title}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                  {entry.role === 'assistant' && entry.interactionId !== undefined ? (
                    <div className="tcvs-help-feedback-action">
                      <button
                        type="button"
                        title="Report an unhelpful answer"
                        disabled={
                          entry.feedbackState === 'submitting' ||
                          entry.feedbackState === 'submitted'
                        }
                        onClick={() => void reportUnhelpful(index)}
                      >
                        {entry.feedbackState === 'submitted'
                          ? 'Reported for review'
                          : entry.feedbackState === 'submitting'
                            ? 'Reporting...'
                            : 'This did not help'}
                      </button>
                      {entry.feedbackState === 'error' ? (
                        <span>Could not report. Try again.</span>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              ))}
              {submitting ? (
                <p className="tcvs-help-thinking">Checking TCVS guidance and live status...</p>
              ) : null}
              {error === null ? null : <p className="tcvs-help-error">{error}</p>}
            </div>

            <form
              className="tcvs-help-form"
              onSubmit={(event) => {
                event.preventDefault();
                void ask();
              }}
            >
              <label htmlFor="tcvs-help-question">Ask about TCVS</label>
              <textarea
                id="tcvs-help-question"
                ref={inputRef}
                rows={3}
                maxLength={HELP_QUESTION_MAX_LENGTH}
                value={question}
                disabled={submitting}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void ask();
                  }
                }}
              />
              <div>
                {entries.length > 0 ? (
                  <button
                    type="button"
                    className="tcvs-btn-secondary"
                    disabled={submitting}
                    onClick={() => {
                      setEntries([]);
                      setError(null);
                    }}
                  >
                    Clear
                  </button>
                ) : (
                  <span />
                )}
                <button type="submit" disabled={submitting || question.trim() === ''}>
                  {submitting ? 'Checking...' : 'Ask'}
                </button>
              </div>
            </form>
          </aside>
        </>
      ) : null}
    </>
  );
}
